import { processQuestionSet } from '../utils/questionProcessor';

// Cache for loaded question sets
const questionSetCache = {};

/**
 * Loads the manifest file that lists all available question sets
 * @returns {Promise<Array>} - Array of question set metadata
 */
export const loadManifest = async () => {
  try {
    const response = await fetch('/data/manifest.json');
    if (!response.ok) {
      throw new Error('Failed to load manifest');
    }
    const manifest = await response.json();
    return manifest.questionSets || [];
  } catch (error) {
    console.error('Error loading manifest:', error);
    return [];
  }
};

/**
 * Loads a specific question set from the public/data folder
 * @param {string} filename - The filename of the question set (e.g., 'genika.json')
 * @returns {Promise<Object>} - The raw question set data
 */
export const loadQuestionSetFile = async (filename) => {
  // Check cache first
  if (questionSetCache[filename]) {
    return questionSetCache[filename];
  }

  try {
    const response = await fetch(`/data/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load question set: ${filename}`);
    }
    const data = await response.json();
    
    // Cache the loaded data
    questionSetCache[filename] = data;
    
    return data;
  } catch (error) {
    console.error(`Error loading question set ${filename}:`, error);
    return null;
  }
};

/**
 * Loads all available question sets from the manifest
 * @returns {Promise<Array>} - Array of question set objects with id and data
 */
export const loadAllQuestionSets = async () => {
  const manifest = await loadManifest();
  
  const questionSets = await Promise.all(
    manifest.map(async (setInfo) => {
      const data = await loadQuestionSetFile(setInfo.file);
      return {
        id: setInfo.id,
        data: data,
        description: setInfo.description
      };
    })
  );
  
  // Filter out any failed loads
  return questionSets.filter(set => set.data !== null);
};

/**
 * Gets a specific question set by ID (loads from manifest first)
 * @param {string} id - The ID of the question set
 * @returns {Promise<Object|null>} - The raw question set data
 */
export const getQuestionSet = async (id) => {
  const manifest = await loadManifest();
  const setInfo = manifest.find(s => s.id === id);
  
  if (!setInfo) {
    console.error(`Question set with ID ${id} not found in manifest`);
    return null;
  }
  
  return await loadQuestionSetFile(setInfo.file);
};

/**
 * Gets a randomized question set by ID
 * This should be called when starting a new game
 * @param {string} id - The ID of the question set
 * @returns {Promise<Object>} - Processed question set with randomized questions
 */
export const getRandomizedQuestionSet = async (id) => {
  const rawData = await getQuestionSet(id);
  if (!rawData) return null;
  
  // Process the raw data to generate random questions
  return processQuestionSet(rawData);
};

// Export for backward compatibility (though not used with async loading)
const dataExports = {
  loadManifest,
  loadAllQuestionSets,
  getQuestionSet,
  getRandomizedQuestionSet
};

export default dataExports;
